import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
// @ts-ignore
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Contract, ContractTemplate } from '../../types/contracts';
import ContractTemplates from './ContractTemplates';

const ContractsList: React.FC = () => {
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [loading, setLoading] = useState(true);
  const [showTemplateManager, setShowTemplateManager] = useState(false);
  const [selectedContract, setSelectedContract] = useState<Contract | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showSendModal, setShowSendModal] = useState(false);
  const [sendLoading, setSendLoading] = useState(false);
  const [sendData, setSendData] = useState({
    recipient_email: '',
    recipient_name: '',
    message: ''
  });
  const [isEditingContent, setIsEditingContent] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [saveLoading, setSaveLoading] = useState(false);

  // Rich text editor configuration for contract content editing
  const quillModules = {
    toolbar: [
      ['bold', 'italic', 'underline'],
      [{ 'header': [1, 2, 3, false] }],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      ['link'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'align': [] }],
      ['clean']
    ],
  };

  const quillFormats = [
    'bold', 'italic', 'underline', 'header', 'list', 'bullet',
    'link', 'color', 'background', 'align'
  ];

  // Handle ESC key press
  const handleEscKey = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      if (isEditingContent) {
        handleCancelEditing();
      }
    }
  }, [isEditingContent]);

  useEffect(() => {
    fetchContracts();
  }, []);

  useEffect(() => {
    // Add event listener when editing
    if (isEditingContent) {
      document.addEventListener('keydown', handleEscKey);
    }
    
    // Cleanup event listener
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isEditingContent, handleEscKey]);

  const fetchContracts = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // For now, we'll get all contracts across all deals
      // In the future, we might want to add pagination or filtering
      const response = await axios.get('/api/contracts', {
        headers: { 'x-auth-token': token }
      });
      
      setContracts(response.data || []);
    } catch (error) {
      console.error('Error fetching contracts:', error);
      setContracts([]);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: Contract['status']) => {
    const statusConfig = {
      draft: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Draft' },
      sent: { bg: 'bg-blue-100', text: 'text-blue-800', label: 'Sent' },
      signed: { bg: 'bg-green-100', text: 'text-green-800', label: 'Signed' },
      rejected: { bg: 'bg-red-100', text: 'text-red-800', label: 'Rejected' },
      expired: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Expired' }
    };

    const config = statusConfig[status] || statusConfig.draft;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const isExpiringSoon = (expiryDate?: string) => {
    if (!expiryDate) return false;
    const expiry = new Date(expiryDate);
    const today = new Date();
    const daysUntilExpiry = Math.ceil((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry <= 7 && daysUntilExpiry > 0;
  };

  const isExpired = (expiryDate?: string) => {
    if (!expiryDate) return false;
    return new Date(expiryDate) < new Date();
  };

  const filteredContracts = contracts.filter(contract => {
    const matchesSearch = contract.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         contract.deal_title?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || contract.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleResendContract = async (contractId: number) => {
    // TODO: Implement resend functionality
    console.log('Resend contract:', contractId);
  };

  const handleDownloadContract = async (contractId: number) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/api/contracts/${contractId}/pdf`, {
        headers: { 'x-auth-token': token },
        responseType: 'blob'
      });

      // Create blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `signed-contract-${contractId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading contract:', error);
      alert('Failed to download contract PDF');
    }
  };

  const handleViewContract = (contract: Contract) => {
    setSelectedContract(contract);
    setEditedContent(contract.content);
    setIsEditingContent(false);
    // Pre-populate send form with lead email if available
    if (contract.lead_email) {
      setSendData(prev => ({
        ...prev,
        recipient_email: contract.lead_email || '',
        recipient_name: `${contract.first_name || ''} ${contract.last_name || ''}`.trim() || ''
      }));
    }
  };

  const handleStartEditing = () => {
    if (selectedContract) {
      setEditedContent(selectedContract.content);
      setIsEditingContent(true);
    }
  };

  const handleCancelEditing = () => {
    setIsEditingContent(false);
    if (selectedContract) {
      setEditedContent(selectedContract.content);
    }
  };

  const handleSaveContent = async () => {
    if (!selectedContract) return;

    setSaveLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`/api/contracts/${selectedContract.id}`, {
        content: editedContent
      }, {
        headers: { 'x-auth-token': token }
      });

      // Update the contract in state
      const updatedContract = response.data;
      setContracts(prev => prev.map(contract => 
        contract.id === selectedContract.id ? updatedContract : contract
      ));
      setSelectedContract(updatedContract);
      setIsEditingContent(false);
      
      alert('Contract content updated successfully!');
    } catch (error: any) {
      console.error('Error updating contract content:', error);
      alert('Failed to update contract content: ' + (error.response?.data?.message || 'Unknown error'));
    } finally {
      setSaveLoading(false);
    }
  };

  const handleSendContract = async () => {
    if (!selectedContract || !sendData.recipient_email.trim() || !sendData.recipient_name.trim()) {
      alert('Please fill in all required fields');
      return;
    }

    setSendLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`/api/contracts/${selectedContract.id}/send`, {
        recipient_email: sendData.recipient_email.trim(),
        recipient_name: sendData.recipient_name.trim(),
        message: sendData.message || `Please review and sign the attached contract: ${selectedContract.title}`
      }, {
        headers: { 'x-auth-token': token }
      });

      // Update the contract status locally
      setContracts(prev => prev.map(contract => 
        contract.id === selectedContract.id 
          ? { ...contract, status: 'sent' as Contract['status'] }
          : contract
      ));

      // Update selected contract
      setSelectedContract(prev => prev ? { ...prev, status: 'sent' as Contract['status'] } : null);

      // Close send modal and reset form
      setShowSendModal(false);
      setSendData({ recipient_email: '', recipient_name: '', message: '' });
      
      alert('Contract sent successfully!');
    } catch (error: any) {
      console.error('Error sending contract:', error);
      alert('Failed to send contract: ' + (error.response?.data?.message || 'Unknown error'));
    } finally {
      setSendLoading(false);
    }
  };

  if (showTemplateManager) {
    return (
      <div>
        <div className="mb-4">
          <button
            onClick={() => setShowTemplateManager(false)}
            className="flex items-center text-blue-600 hover:text-blue-800"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Contracts
          </button>
        </div>
        <ContractTemplates />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">Contracts</h2>
          <p className="text-gray-600">Manage and track contract signatures</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => setShowTemplateManager(true)}
            className="bg-gray-100 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-200 transition-colors"
          >
            Manage Templates
          </button>
          <button
            onClick={() => {/* TODO: Open generate contract modal */}}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
          >
            Generate Contract
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search contracts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Status</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="signed">Signed</option>
            <option value="rejected">Rejected</option>
            <option value="expired">Expired</option>
          </select>
        </div>
      </div>

      {/* Contracts List */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {filteredContracts.length === 0 ? (
          <div className="p-6 text-center">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No contracts found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {contracts.length === 0 
                ? "Get started by generating your first contract from a deal."
                : "Try adjusting your search or filter criteria."
              }
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contract
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Deal
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Expiry
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Signed
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredContracts.map((contract) => (
                  <tr 
                    key={contract.id} 
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => handleViewContract(contract)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{contract.title}</div>
                        <div className="text-sm text-gray-500">#{contract.id}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{contract.deal_title || '-'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        {getStatusBadge(contract.status)}
                        {isExpiringSoon(contract.expiry_date) && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-yellow-100 text-yellow-800">
                            <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                            Expiring Soon
                          </span>
                        )}
                        {isExpired(contract.expiry_date) && contract.status === 'sent' && (
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-red-100 text-red-800">
                            Expired
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(contract.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(contract.expiry_date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {contract.signed_at ? (
                        <div>
                          <div>{formatDate(contract.signed_at)}</div>
                          <div className="text-xs text-gray-400">{contract.signed_by_name}</div>
                        </div>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        {contract.status === 'sent' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleResendContract(contract.id);
                            }}
                            className="text-green-600 hover:text-green-900"
                          >
                            Resend
                          </button>
                        )}
                        {contract.status === 'signed' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownloadContract(contract.id);
                            }}
                            className="text-purple-600 hover:text-purple-900"
                          >
                            Download
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Contract Details Modal */}
      {selectedContract && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">{selectedContract.title}</h3>
                <button
                  onClick={() => setSelectedContract(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Status</label>
                    <div className="mt-1">{getStatusBadge(selectedContract.status)}</div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Deal</label>
                    <div className="mt-1 text-sm text-gray-900">{selectedContract.deal_title || 'N/A'}</div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Created</label>
                    <div className="mt-1 text-sm text-gray-900">{formatDate(selectedContract.created_at)}</div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Expiry</label>
                    <div className="mt-1 text-sm text-gray-900">{formatDate(selectedContract.expiry_date)}</div>
                  </div>
                  {selectedContract.signed_at && (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Signed Date</label>
                        <div className="mt-1 text-sm text-gray-900">{formatDate(selectedContract.signed_at)}</div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Signed By</label>
                        <div className="mt-1 text-sm text-gray-900">{selectedContract.signed_by_name}</div>
                      </div>
                    </>
                  )}
                </div>
                
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="block text-sm font-medium text-gray-700">Contract Content</label>
                    {selectedContract.status === 'draft' && !isEditingContent && (
                      <button
                        onClick={handleStartEditing}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center"
                      >
                        <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Edit Content
                      </button>
                    )}
                  </div>
                  
                  {isEditingContent ? (
                    <div>
                      <div className="border border-gray-300 rounded-md overflow-hidden">
                        {React.createElement(ReactQuill as any, {
                          value: editedContent,
                          onChange: (content: string) => setEditedContent(content),
                          modules: quillModules,
                          formats: quillFormats,
                          placeholder: "Enter contract content...",
                          style: { minHeight: '300px' }
                        })}
                      </div>
                      <style>{`
                        .ql-editor {
                          min-height: 300px;
                        }
                        .ql-toolbar {
                          background-color: #f9fafb;
                          border-bottom: 1px solid #e5e7eb;
                        }
                      `}</style>
                      
                      <div className="flex justify-end space-x-2 mt-3">
                        <button
                          onClick={handleCancelEditing}
                          disabled={saveLoading}
                          className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors disabled:opacity-50"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleSaveContent}
                          disabled={saveLoading}
                          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {saveLoading ? 'Saving...' : 'Save Changes'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div 
                      className="mt-1 p-4 border border-gray-300 rounded-md bg-gray-50 max-h-96 overflow-y-auto"
                      dangerouslySetInnerHTML={{ __html: selectedContract.content }}
                    />
                  )}
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setSelectedContract(null)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Close
                </button>
                {selectedContract.status === 'draft' && (
                  <button
                    onClick={() => setShowSendModal(true)}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                  >
                    Send Contract
                  </button>
                )}
                {selectedContract.status === 'sent' && selectedContract.signing_token && (
                  <button
                    onClick={() => {
                      const signingUrl = `${window.location.origin}/public/contracts/sign/${selectedContract.signing_token}`;
                      navigator.clipboard.writeText(signingUrl);
                      alert('Signing URL copied to clipboard!');
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                  >
                    Copy Signing URL
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Send Contract Modal */}
      {showSendModal && selectedContract && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-md shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Send Contract</h3>
                <button
                  onClick={() => setShowSendModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600 mb-4">
                    Send "{selectedContract.title}" for signature
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Recipient Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={sendData.recipient_name}
                    onChange={(e) => setSendData(prev => ({ ...prev, recipient_name: e.target.value }))}
                    placeholder="Full name of the person signing"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Recipient Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    value={sendData.recipient_email}
                    onChange={(e) => setSendData(prev => ({ ...prev, recipient_email: e.target.value }))}
                    placeholder="Email address to send contract to"
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Custom Message (Optional)
                  </label>
                  <textarea
                    value={sendData.message}
                    onChange={(e) => setSendData(prev => ({ ...prev, message: e.target.value }))}
                    placeholder="Optional message to include in the email"
                    rows={3}
                    className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowSendModal(false)}
                  disabled={sendLoading}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSendContract}
                  disabled={sendLoading || !sendData.recipient_email.trim() || !sendData.recipient_name.trim()}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {sendLoading ? 'Sending...' : 'Send Contract'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContractsList;